import { useSelector } from "react-redux";
import {
  type BookedFlightItineraryWithDepartureTime,
  Status,
} from "@b2bportal/air-booking-api";
import { FlightPostBookingSelectors } from "@b2bportal/core-flights";
import { IconNameEnum } from "@b2bportal/core-themes";
import type { TripsAction } from "@b2bportal/core-trips/overridable";
import {
  ProductType,
  type PlatformComponentProps,
} from "@b2bportal/core-types";
import { useI18nContext } from "@hopper-b2b/i18n";
import { FlightDisruptionAlertBanner } from "@tenant/disruption";
import {
  FlightCardNotification,
  FlightScheduleChangeBanner,
} from "@tenant/flights";
import { TripsCard } from "@tenant/trips";
import { AirlineIcon } from "@tenant/ui";
import clsx from "clsx";
import { useFlightItineraryCard } from "./useFlightItineraryCard";
import MultiAirlineIcon from "@/assets/icons/multi-airline.svg";
import { TripsReservationStatusPill } from "@/components";
import { useFlightDisruptionAlertBanner } from "@/hooks/shared/useFlightDisruptionAlertBanner";

interface FlightItineraryCardProps extends PlatformComponentProps {
  itinerary: BookedFlightItineraryWithDepartureTime;
  selectItinerary?: () => void;
  actions?: TripsAction[];
}

export const FlightItineraryCard = (props: FlightItineraryCardProps) => {
  const { children, className, itinerary } = props;
  const {
    text: { title, cardTitle, description, dates },
    handlers: { selectItinerary },
    context: {
      confirmationCode,
      flightStatus,
      scheduleChangeSeverity,
      hasBannerContent,
      airlineCode,
      multicityLegs,
      hasMultipleAirlines,
    },
  } = useFlightItineraryCard(props);
  const { t } = useI18nContext();
  const airportMap = useSelector(FlightPostBookingSelectors.getAirports);
  const {
    handleDisruptionProtectionCta,
    disruptionAlertBannerType,
    disruptionBannerDestinationProps,
    showDisruptionContractBanner,
  } = useFlightDisruptionAlertBanner(itinerary, airportMap);
  const isCancelled = flightStatus === Status.Canceled;
  const dateDisplay = isCancelled ? (
    <span style={{ textDecoration: "line-through" }}>{dates}</span>
  ) : (
    dates
  );

  return (
    <TripsCard
      {...(hasBannerContent && {
        banner: (
          <>
            <FlightCardNotification itinerary={itinerary} />
            {showDisruptionContractBanner && (
              <FlightDisruptionAlertBanner
                onClick={handleDisruptionProtectionCta}
                type={disruptionAlertBannerType}
                productName={t("core-disruption.productName")}
                bannerIcon={IconNameEnum.warningCircle}
                {...disruptionBannerDestinationProps}
              ></FlightDisruptionAlertBanner>
            )}
            <FlightScheduleChangeBanner
              itinerary={itinerary}
              hideActionButton={true}
            />
          </>
        ),
      })}
      className={clsx(className)}
      onClick={selectItinerary}
      title={title}
      confirmationCode={confirmationCode}
      description={description}
      date={dateDisplay}
      status={
        <TripsReservationStatusPill
          variant={ProductType.FLIGHTS}
          status={flightStatus}
          scheduleChangeSeverity={scheduleChangeSeverity}
        />
      }
      imageContent={
        hasMultipleAirlines ? (
          <img src={MultiAirlineIcon} alt={t("multipleAirlines")} />
        ) : airlineCode ? (
          <AirlineIcon airlineCode={airlineCode} size="large" />
        ) : undefined
      }
      actions={props.actions}
      constrainImage
      legs={multicityLegs}
      cardTitle={cardTitle}
    >
      {children}
    </TripsCard>
  );
};
